import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/* ==========================================================================
   CM: VARIANTENWECHSEL IM CART-DRAWER — HILFSFUNKTIONEN
   --------------------------------------------------------------------------
   Markup: snippets/cm-cart-variant-picker.liquid
   Ablauf: CartItemsComponent#onLineVariantChange (weiter unten)

   Zwei Wege, die Variante einer Zeile umzustellen — der Kunde merkt keinen
   Unterschied, ausser bei der Position der Zeile:

   1. Storefront API (cartLinesUpdate mit neuer merchandiseId)
      Tauscht die Variante IN der bestehenden Zeile. Die Zeile bleibt an
      ihrer Position — genau wie beim Kaching-Cart. Laeuft ohne Token
      (Shopify erlaubt Cart-Lesen/-Schreiben tokenlos).
      Wird nur genommen, wenn alles eindeutig ist, und das Ergebnis wird
      geprueft. Klappt irgendetwas nicht (Endpunkt gesperrt, Bot-Schutz,
      Feld wird ignoriert), wird der Weg fuer den Rest der Sitzung
      abgeschaltet und es geht sofort mit Weg 2 weiter.

   2. Ajax-Cart-API (add.js, danach change.js mit Menge 0)
      Funktioniert immer. Shopify sortiert neu hinzugefuegte Zeilen nach
      oben, die umgestellte Zeile steht danach also oben.
      Erst wird hinzugefuegt, dann geloescht: scheitert das Hinzufuegen
      (z. B. ausverkauft), bleibt der Warenkorb unveraendert. Scheitert
      erst das Loeschen, wird das Hinzugefuegte zurueckgenommen.

   In JEDEM Fehlerfall, in dem der Warenkorb sich veraendert haben koennte,
   wird der Drawer frisch vom Server geholt. Angezeigt wird damit immer der
   echte Stand — nie ein geratener.
   ========================================================================== */

/** Storefront-API-Version fuer den Weg 1 (stabil bis mind. Mitte 2027). */
const CM_SFAPI_VERSION = '2026-07';
/** sessionStorage-Schalter: Weg 1 fuer diese Sitzung aus. */
const CM_SFAPI_SESSION_KEY = 'cm:cart-variant-sfapi';
/** Obergrenze pro Request. Danach gilt er als gescheitert. */
const CM_REQUEST_TIMEOUT_MS = 15000;

const CM_SFAPI_LINES_QUERY = `query CmCartLines($id: ID!) {
  cart(id: $id) {
    id
    lines(first: 100) {
      nodes {
        id
        quantity
        attributes { key value }
        merchandise { ... on ProductVariant { id } }
      }
    }
  }
}`;

const CM_SFAPI_LINE_VARIANT_MUTATION = `mutation CmCartLineVariant($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
  cartLinesUpdate(cartId: $cartId, lines: $lines) {
    cart {
      id
      lines(first: 100) {
        nodes {
          id
          quantity
          merchandise { ... on ProductVariant { id } }
        }
      }
    }
    userErrors { code field message }
    warnings { code message }
  }
}`;

/**
 * Fehler beim Variantenwechsel.
 *  kind 'user'    -> Warenkorb unveraendert, Meldung anzeigen (z. B. ausverkauft)
 *  kind 'stale'   -> Zeile gibt es so nicht mehr (anderer Tab) -> neu laden
 *  kind 'unknown' -> Ausgang offen -> neu laden, Meldung anzeigen
 */
class CmVariantSwapError extends Error {
  /**
   * @param {'user' | 'stale' | 'unknown'} kind
   * @param {string | null} [userMessage]
   */
  constructor(kind, userMessage = null) {
    super(userMessage || `cart variant swap failed (${kind})`);
    this.name = 'CmVariantSwapError';
    this.kind = kind;
    this.userMessage = userMessage;
  }

  /** Muss der Drawer danach frisch vom Server geholt werden? */
  get cartMayHaveChanged() {
    return this.kind !== 'user';
  }
}

/**
 * fetch mit Zeitlimit. Ohne Limit kann ein haengender Request den Drawer
 * beliebig lange gesperrt halten.
 * @param {string} url
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
async function cmFetch(url, init = {}) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), CM_REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { credentials: 'same-origin', ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Liest eine Antwort als JSON, ohne bei kaputtem Inhalt zu werfen.
 * @param {Response} response
 * @returns {Promise<any>}
 */
async function cmReadJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/** @param {number | string} id */
function cmVariantGid(id) {
  return `gid://shopify/ProductVariant/${id}`;
}

/**
 * Line-Item-Properties (Ajax) und Line-Attributes (Storefront API) sind
 * dieselben Daten. Leere Werte zaehlen nicht mit.
 * @param {Array<{key: string, value: any}>} entries
 * @returns {string}
 */
function cmAttributeSignature(entries) {
  return entries
    .filter((entry) => entry && entry.key && entry.value !== null && entry.value !== undefined && entry.value !== '')
    .map((entry) => {
      const value = typeof entry.value === 'string' ? entry.value : JSON.stringify(entry.value);
      return `${entry.key}\u0000${value}`;
    })
    .sort()
    .join('\u0001');
}

/**
 * @param {Record<string, any> | null | undefined} properties
 * @returns {string}
 */
function cmPropertiesSignature(properties) {
  return cmAttributeSignature(Object.entries(properties || {}).map(([key, value]) => ({ key, value })));
}

/**
 * @param {Array<{key: string, value: any}> | null | undefined} attributes
 * @param {Record<string, any> | null | undefined} properties
 */
function cmAttributesMatch(attributes, properties) {
  return cmAttributeSignature(Array.isArray(attributes) ? attributes : []) === cmPropertiesSignature(properties);
}

/**
 * Von Shopify formulierte Fehlermeldung (bereits in der Shop-Sprache),
 * z. B. "You can only add 2 of this item to your cart."
 * @param {any} json
 * @returns {string | null}
 */
function cmCartErrorMessage(json) {
  if (!json || typeof json !== 'object') return null;
  if (typeof json.description === 'string' && json.description.trim()) return json.description.trim();
  if (typeof json.errors === 'string' && json.errors.trim()) return json.errors.trim();
  return null;
}

/**
 * Properties fuer add.js — leere Werte fallen weg, alles andere (auch
 * versteckte "_"-Properties von Apps) wird 1:1 uebernommen.
 * @param {Record<string, any> | null | undefined} properties
 * @returns {Record<string, any> | null}
 */
function cmCleanProperties(properties) {
  if (!properties || typeof properties !== 'object') return null;
  /** @type {Record<string, any>} */
  const clean = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === null || value === undefined || value === '') continue;
    clean[key] = value;
  }
  return Object.keys(clean).length ? clean : null;
}

/**
 * @typedef {Object} CmCartVariant
 * @property {number} id
 * @property {boolean} available
 * @property {string[]} options
 * @property {string} image
 */

/**
 * Liest die Variantendaten einer Zeile (JSON aus dem Liquid-Snippet).
 * @param {HTMLElement} picker
 * @returns {{ variants: CmCartVariant[], images: Record<string, string> } | null}
 */
function readCartVariantData(picker) {
  const script = picker.querySelector('script[data-cm-variant-json]');
  if (!script?.textContent) return null;

  try {
    const raw = JSON.parse(script.textContent);
    if (!raw || !Array.isArray(raw.v)) return null;

    const variants = raw.v
      .filter((entry) => Array.isArray(entry) && entry.length >= 3)
      .map((entry) => ({
        id: Number(entry[0]),
        available: entry[1] === 1,
        options: Array.isArray(entry[2]) ? entry[2].map((value) => String(value)) : [],
        image: entry[3] ? String(entry[3]) : '',
      }))
      .filter((variant) => Number.isFinite(variant.id) && variant.id > 0);

    return { variants, images: raw.i && typeof raw.i === 'object' ? raw.i : {} };
  } catch (error) {
    console.warn('[cart-items] Variantendaten nicht lesbar:', error);
    return null;
  }
}

/**
 * Bestimmt die Zielvariante nach einer Auswahl.
 *
 * Fest stehen die geaenderte Option und alle Optionen DAVOR. Fuer die
 * Optionen danach wird die aktuelle Auswahl bevorzugt — fruehere Optionen
 * wiegen schwerer. Gibt es die exakte Kombination nicht lieferbar, wird die
 * naechstbeste lieferbare genommen (z. B. Farbe gewechselt, Groesse 4 gibt
 * es darin nicht -> naechste lieferbare Groesse).
 * Spiegelt die disabled-Logik in snippets/cm-cart-variant-picker.liquid.
 *
 * @param {CmCartVariant[]} variants
 * @param {string[]} selection
 * @param {number} changedIndex
 * @returns {CmCartVariant | null}
 */
function resolveCartVariant(variants, selection, changedIndex) {
  const fixed = selection.slice(0, changedIndex + 1);

  /** @type {CmCartVariant | null} */
  let best = null;
  let bestScore = -1;

  for (const variant of variants) {
    if (!variant.available) continue;
    if (!fixed.every((value, index) => variant.options[index] === value)) continue;

    let score = 0;
    for (let index = changedIndex + 1; index < selection.length; index++) {
      if (variant.options[index] === selection[index]) score += 2 ** (selection.length - index);
    }

    // Gleichstand: die Reihenfolge im Shop entscheidet (erste gewinnt).
    if (score > bestScore) {
      best = variant;
      bestScore = score;
    }
  }

  return best;
}

/** Bereits vorgeladene Bilder (verhindert doppelte Requests). */
const cmPreloadedImages = new Map();

/**
 * Laedt das Bild der neuen Variante, waehrend der Warenkorb noch rechnet.
 * Steht die neue Zeile, liegt das Bild schon im Cache und poppt nicht nach.
 * @param {string | undefined} url
 */
function cmPreloadImage(url) {
  if (!url || cmPreloadedImages.has(url)) return;
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  cmPreloadedImages.set(url, image);
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    // CM: War 300ms - so lange ging nach +/- gar kein Request raus.
    // 150ms buendelt schnelles Tippen weiterhin, reagiert aber direkter
    // (gleicher Wert wie im Cole-&-May-Theme).
    150
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * CM: HTML, das zuletzt per Morph eingespielt wurde. Verhindert, dass derselbe
   * Stand zweimal gemorpht wird (einmal frueh ueber 'cm:cart-sections-ready',
   * einmal regulaer ueber das Event-Promise).
   * @type {string | null}
   */
  #lastAppliedHtml = null;

  /**
   * CM: SCHNELLERES BEFUELLEN DES DRAWERS NACH ADD-TO-CART
   * ------------------------------------------------------------------
   * Die Antwort von /cart/add.js enthaelt das fertig gerenderte Drawer-HTML
   * bereits. Bisher wurde trotzdem erst noch /cart.json abgewartet (zweiter
   * Request), bevor das Promise aufloeste und der Drawer gefuellt wurde.
   * assets/product-form.js meldet die Sections jetzt sofort per
   * 'cm:cart-sections-ready' — der Drawer wird damit einen kompletten
   * Request frueher befuellt.
   * @param {Event} event
   */
  #handleEarlySections = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const html = detail?.sections?.[this.sectionId];
    if (!html) return;
    this.#applySectionHtml(html).catch((error) => {
      console.warn('[cart-items] early morph failed:', error);
    });
  };

  /**
   * CM: Laeuft gerade ein Variantenwechsel? Solange ja, sind Menge, Loeschen
   * und weitere Wechsel gesperrt — zwei gleichzeitige Umbauten am Warenkorb
   * koennten sich sonst gegenseitig die Zeilennummern verschieben.
   */
  #variantSwapBusy = false;

  /**
   * CM: Laufende Mengen-Requests dieses Components. Ein Variantenwechsel
   * wartet, bis sie durch sind, damit er mit der echten Menge arbeitet.
   * @type {Set<Promise<unknown>>}
   */
  #inflightRequests = new Set();

  /** CM: Zurueck-Cache (bfcache) — Dropdowns auf den gerenderten Stand setzen. */
  #handlePageShow = () => {
    this.#syncVariantSelects(true);
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cm:cart-sections-ready', this.#handleEarlySections);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);

    this.#syncVariantSelects(true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('cm:cart-sections-ready', this.#handleEarlySections);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * CM: Nach jedem Morph (Menge, Rabatt, externe Updates) die Dropdowns auf
   * den Server-Stand setzen. morph.js gleicht <option selected> zwar ab, das
   * hier ist die zweite Absicherung — z. B. nach Formular-Wiederherstellung
   * durch den Browser.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#syncVariantSelects();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    // CM: Waehrend eines Variantenwechsels keine Mengenaenderung — die
    // Zeilennummern stimmen erst nach dem Wechsel wieder. Der naechste
    // Morph setzt die angezeigte Menge auf den echten Stand zurueck.
    if (this.#variantSwapBusy) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    // CM: siehe #onQuantityChange — waehrend eines Variantenwechsels gesperrt.
    if (this.#variantSwapBusy) return;

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      // CM: LOGO-BLITZER BEIM LEEREN DES WARENKORBS
      // ------------------------------------------------------------------
      // Der Kopf mit dem Logo liegt AUSSERHALB dieses Components. Er blieb
      // deshalb stehen, bis die Server-Antwort den leeren Kopf nachlieferte,
      // und wurde vom View-Transition sogar noch sichtbar ausgeblendet.
      // Jetzt wird er VOR dem Transition in den Leer-Zustand versetzt, damit
      // der alte Schnappschuss das Logo gar nicht mehr enthaelt.
      if (this.isDrawer) {
        const inner = this.closest('.cart-drawer__inner');
        inner?.classList.add('cart-drawer--empty');
        inner?.querySelectorAll('.cart-drawer__brand').forEach((brand) => brand.remove());
        inner?.querySelector('.cart-drawer__header')?.classList.add('cart-drawer__header--empty');
      }

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
        this.#lastAppliedHtml = null;
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    // CM: Request merken, damit ein Variantenwechsel darauf warten kann.
    const request = fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        this.#lastAppliedHtml = parsedResponseText.sections[this.sectionId];
        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });

    this.#trackRequest(request);
  }

  /**
   * CM: Merkt sich einen laufenden Warenkorb-Request bis zu seinem Ende.
   * @param {Promise<unknown>} request
   */
  #trackRequest(request) {
    this.#inflightRequests.add(request);
    const forget = () => this.#inflightRequests.delete(request);
    request.then(forget, forget);
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];

        if (cartItemsHtml) {
          // CM: Wurde exakt dieser Stand schon frueh eingespielt, nicht
          // nochmal morphen (sonst zuckt die neue Zeile ein zweites Mal).
          if (cartItemsHtml === this.#lastAppliedHtml) {
            this.#updateCartQuantitySelectorButtonStates();
            return;
          }
          await this.#applySectionHtml(cartItemsHtml);
        } else {
          const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
            injectStylesheet: wasEmptyCartDrawer,
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * CM: Spielt neues Section-HTML ein (gemeinsamer Weg fuer frueh und regulaer).
   * @param {string} cartItemsHtml
   */
  async #applySectionHtml(cartItemsHtml) {
    if (cartItemsHtml === this.#lastAppliedHtml) return;
    this.#lastAppliedHtml = cartItemsHtml;

    // Animate empty → non-empty in the drawer (possible in squeeze mode
    // where the page is interactive alongside the open drawer). This also
    // needs the response stylesheet because it adds the cart summary markup.
    const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
    /** @type {'hydration' | 'full'} */
    const mode = this.isDrawer ? 'hydration' : 'full';
    const morphOptions = {
      mode,
      injectStylesheet: wasEmptyCartDrawer,
    };

    // CM: Laeuft gerade der Platzhalter (erster Artikel), ist der echte
    // Inhalt ohnehin verdeckt. Ein View-Transition wuerde dann nur die ganze
    // Seite kurz einfrieren — die weiche Einblendung uebernimmt
    // assets/cart-drawer.js (data-cart-revealing).
    const isPriming = this.closest('cart-drawer-component')?.hasAttribute('data-cart-priming') ?? false;

    const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

    if (wasEmptyCartDrawer && !isPriming) {
      startViewTransition(() => {
        morphSection(this.sectionId, cartItemsHtml, morphOptions);
      }, ['fill-cart-drawer']);
    } else {
      await morphSection(this.sectionId, cartItemsHtml, morphOptions);
    }

    // Animate newly added rows (reverse of the remove animation).
    if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
      for (const row of this.refs.cartItemRows ?? []) {
        if (!existingKeys.has(row.dataset.key)) {
          row.classList.add('adding');
          onAnimationEnd(row, () => row.classList.remove('adding'));
        }
      }
    }

    // Update button states for all cart quantity selectors after morph
    this.#updateCartQuantitySelectorButtonStates();

    this.dispatchEvent(new CustomEvent('cm:cart-sections-applied', { bubbles: true }));
  }

  /* ========================================================================
     CM: VARIANTENWECHSEL IM CART-DRAWER
     ------------------------------------------------------------------------
     Ablauf bei jeder Auswahl in einem der Dropdowns:

       1. Zielvariante bestimmen (resolveCartVariant). Sonderfaelle:
          gleiche Variante -> nichts tun; nichts lieferbar -> Meldung.
       2. Sperren: Dropdowns, Menge, Loeschen. Zeile gedimmt, Preis
          schimmert, Bild der neuen Variante wird vorgeladen.
       3. Laufende Mengen-Requests abwarten, dann /cart.js frisch lesen —
          gearbeitet wird immer mit dem echten Stand (Menge, Properties,
          Abo), nie mit dem, was gerade im DOM steht.
       4. Umstellen: Storefront API (Zeile bleibt an ihrer Position) oder
          Ajax-API (Rueckfall, funktioniert immer). Siehe Kopf der Datei.
       5. Drawer mit der Server-Antwort morphen — Bild, Preis, Rabatte,
          Fortschrittsbalken, Summe kommen damit alle frisch vom Server.
       6. Event an Cart-Icon, Upsell-Widget usw. melden, Fokus und
          Scrollposition wiederherstellen.

     Jeder Fehler endet in einem definierten Zustand:
       - Warenkorb sicher unveraendert -> Dropdowns zurueck + Meldung
       - Warenkorb evtl. veraendert    -> Drawer frisch vom Server + Meldung
     ======================================================================== */

  /**
   * on:change der Varianten-Dropdowns (snippets/cm-cart-variant-picker.liquid).
   * @param {Event} event
   */
  onLineVariantChange(event) {
    const select = event?.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const picker = select.closest('[data-cm-variant-picker]');
    if (!(picker instanceof HTMLElement) || !this.contains(picker)) return;

    this.#handleVariantSelection(select, picker).catch((error) => {
      console.error('[cart-items] Variantenwechsel fehlgeschlagen:', error);
    });
  }

  /**
   * @param {HTMLSelectElement} select - Das geaenderte Dropdown
   * @param {HTMLElement} picker - [data-cm-variant-picker] der Zeile
   */
  async #handleVariantSelection(select, picker) {
    const selects = this.#getPickerSelects(picker);

    // Ein Wechsel laeuft schon: diese Auswahl verwerfen statt sie zu stapeln.
    if (this.#variantSwapBusy) {
      this.#resetPickerSelects(selects);
      return;
    }

    const row = picker.closest('tr[data-key]');
    const lineKey = picker.dataset.lineKey ?? '';
    const fromVariantId = Number(picker.dataset.variantId);
    const data = readCartVariantData(picker);

    if (!(row instanceof HTMLElement) || !lineKey || !fromVariantId || !data) {
      this.#resetPickerSelects(selects);
      return;
    }

    const changedIndex = Math.max(0, Number(select.dataset.optionIndex) || 0);
    const selection = selects.map((element) => element.value);
    const target = resolveCartVariant(data.variants, selection, changedIndex);

    if (!target) {
      this.#resetPickerSelects(selects);
      this.#showRowError(row, picker.dataset.soldOutText);
      return;
    }

    if (target.id === fromVariantId) {
      this.#resetPickerSelects(selects);
      return;
    }

    // Sofort zeigen, worauf umgestellt wird — inkl. Optionen, die beim
    // Farbwechsel automatisch nachgezogen werden (z. B. naechste Groesse).
    selects.forEach((element, index) => {
      const value = target.options[index];
      if (value !== undefined && Array.from(element.options).some((option) => option.value === value)) {
        element.value = value;
      }
    });

    cmPreloadImage(data.images[target.image]);

    await this.#swapLineVariant({
      row,
      picker,
      lineKey,
      toVariantId: target.id,
      focusIndex: changedIndex,
      restoreFocus: document.activeElement === select,
      quantityHint: Math.max(1, Number(picker.dataset.quantity) || 1),
    });
  }

  /**
   * @param {Object} config
   * @param {HTMLElement} config.row
   * @param {HTMLElement} config.picker
   * @param {string} config.lineKey
   * @param {number} config.toVariantId
   * @param {number} config.focusIndex
   * @param {boolean} config.restoreFocus
   * @param {number} config.quantityHint
   */
  async #swapLineVariant({ row, picker, lineKey, toVariantId, focusIndex, restoreFocus, quantityHint }) {
    this.#variantSwapBusy = true;
    this.#debouncedOnChange.cancel();

    const performanceMarker = cartPerformance.createStartingMarker('variant-change:user-action');
    const sectionIds = this.#getSectionIdsToUpdate();
    const lockedSelects = this.#lockVariantSelects();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.#hideRowError(row);
    this.#setRowLoading(row, true);
    this.#disableCartItems();
    /** @type {any} */ (row.querySelector('text-component'))?.shimmer?.();
    /** @type {any} */ (this.refs.cartTotal)?.shimmer?.();

    this.#dispatchVariantSwapEvent(lineKey, toVariantId, quantityHint, deferred.promise);

    /** @type {string | null} */
    let errorMessage = null;

    try {
      await this.#waitForInflightRequests();

      const outcome = await this.#performVariantSwap(lineKey, toVariantId, sectionIds);
      const state = outcome.cart && outcome.sections ? outcome : await this.#loadCartState(sectionIds);

      await this.#applyCartState(state);
      this.#resolveSwapEvent(deferred, state);
      this.#afterVariantSwap(toVariantId, focusIndex, restoreFocus, outcome.notice ?? null);
    } catch (rawError) {
      const error = rawError instanceof CmVariantSwapError ? rawError : new CmVariantSwapError('unknown');
      if (!(rawError instanceof CmVariantSwapError)) {
        console.error('[cart-items] Variantenwechsel:', rawError);
      }

      let resolved = false;

      // Ausgang offen -> echten Stand vom Server holen und anzeigen.
      if (error.cartMayHaveChanged) {
        try {
          const state = await this.#loadCartState(sectionIds);
          await this.#applyCartState(state);
          this.#resolveSwapEvent(deferred, state);
          resolved = true;
        } catch (refreshError) {
          console.error('[cart-items] Warenkorb konnte nicht neu geladen werden:', refreshError);
        }
      }

      if (!resolved) {
        this.#resetPickerSelects(this.#getPickerSelects(picker));
        deferred.reject(error);
      }

      // 'stale' = Zeile wurde woanders geaendert; der neue Stand ist bereits
      // sichtbar, eine Fehlermeldung waere hier nur verwirrend.
      if (error.kind !== 'stale') {
        errorMessage = error.userMessage || picker.dataset.errorText || null;

        this.dispatchEvent(
          new CartErrorEvent({
            error: error.userMessage || error.message || 'Failed to change variant',
            code: error.kind === 'user' ? 'INVALID' : 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#variantSwapBusy = false;
      this.#unlockVariantSelects(lockedSelects);
      this.#setRowLoading(row, false);
      this.#enableCartItems();
      resetShimmer(this);
      cartPerformance.measureFromMarker(performanceMarker);
    }

    if (errorMessage) {
      // Nach einem Neuladen kann die Zeile ersetzt worden sein.
      this.#showRowError(row.isConnected ? row : this.#findRowByKey(lineKey), errorMessage);
    }
  }

  /**
   * Stellt die Variante um. Gibt entweder den fertigen Stand zurueck
   * (Ajax-Weg: Warenkorb + Sections aus change.js) oder nur das Signal,
   * dass der Stand frisch geladen werden muss.
   * @param {string} lineKey
   * @param {number} toVariantId
   * @param {string[]} sectionIds
   * @returns {Promise<{ cart?: any, sections?: Record<string, string> | null, notice?: string | null }>}
   */
  async #performVariantSwap(lineKey, toVariantId, sectionIds) {
    const cart = await this.#getCartJson();
    const line = cart.items.find((/** @type {any} */ item) => item.key === lineKey);

    // Zeile gibt es so nicht mehr (anderer Tab, App) -> nur neu anzeigen.
    if (!line) throw new CmVariantSwapError('stale');

    // Steht schon auf der Zielvariante -> nur neu anzeigen.
    if (Number(line.variant_id) === toVariantId) return { sections: null };

    if (this.#canUseStorefrontApi(cart, line, toVariantId)) {
      const result = await this.#swapViaStorefrontApi(cart, line, toVariantId);

      if (result.status === 'applied') return { sections: null, notice: result.notice ?? null };
      if (result.status === 'ambiguous') throw new CmVariantSwapError('stale');
      // 'skipped' -> Ajax-Weg
    }

    return this.#swapViaAjax(line, toVariantId, sectionIds);
  }

  /* ---------------------------------------------------- Weg 2: Ajax-API */

  /**
   * add.js (neue Variante, gleiche Menge/Properties/Abo) -> change.js (alte
   * Zeile auf 0, liefert direkt den neu gerenderten Drawer mit).
   * @param {any} line - Zeile aus /cart.js
   * @param {number} toVariantId
   * @param {string[]} sectionIds
   */
  async #swapViaAjax(line, toVariantId, sectionIds) {
    /** @type {Record<string, any>} */
    const item = { id: toVariantId, quantity: line.quantity };

    const properties = cmCleanProperties(line.properties);
    if (properties) item.properties = properties;

    const sellingPlanId = line.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlanId) item.selling_plan = sellingPlanId;

    let added;
    try {
      added = await this.#postCart(Theme.routes.cart_add_url, { items: [item] });
    } catch {
      // Netz/Zeitlimit: unklar, ob Shopify es noch verarbeitet hat.
      throw new CmVariantSwapError('unknown');
    }

    // Abgelehnt (z. B. ausverkauft, Hoechstmenge) -> Warenkorb unveraendert.
    if (!added.ok) throw new CmVariantSwapError('user', cmCartErrorMessage(added.json));

    const removeBody = {
      id: line.key,
      quantity: 0,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    /** @type {{ ok: boolean, status: number, json: any } | null} */
    let removed = null;
    for (let attempt = 0; attempt < 2 && !removed?.ok; attempt++) {
      try {
        removed = await this.#postCart(Theme.routes.cart_change_url, removeBody);
      } catch {
        removed = null;
      }
    }

    if (removed?.ok) {
      return removed.json?.sections ? { cart: removed.json, sections: removed.json.sections } : { sections: null };
    }

    // Loeschen hat (scheinbar) nicht geklappt. Erst pruefen, ob es nicht doch
    // durchging — sonst das Hinzugefuegte zuruecknehmen.
    const rollback = await this.#rollbackAddedLine(line, toVariantId);
    if (rollback === 'already-removed') return { sections: null };

    throw new CmVariantSwapError('unknown', cmCartErrorMessage(removed?.json));
  }

  /**
   * @param {any} line - Die urspruengliche Zeile aus /cart.js
   * @param {number} toVariantId
   * @returns {Promise<'already-removed' | 'rolled-back' | 'nothing' | 'failed'>}
   */
  async #rollbackAddedLine(line, toVariantId) {
    try {
      const cart = await this.#getCartJson();

      if (!cart.items.some((/** @type {any} */ item) => item.key === line.key)) return 'already-removed';

      const signature = cmPropertiesSignature(line.properties);
      const addedLine = cart.items.find(
        (/** @type {any} */ item) =>
          Number(item.variant_id) === toVariantId && cmPropertiesSignature(item.properties) === signature
      );
      if (!addedLine) return 'nothing';

      const quantity = Math.max(0, Number(addedLine.quantity) - Number(line.quantity));
      const result = await this.#postCart(Theme.routes.cart_change_url, { id: addedLine.key, quantity });
      return result.ok ? 'rolled-back' : 'failed';
    } catch {
      return 'failed';
    }
  }

  /* ------------------------------------------- Weg 1: Storefront API */

  #storefrontApiAllowed() {
    try {
      return window.sessionStorage.getItem(CM_SFAPI_SESSION_KEY) !== 'off';
    } catch {
      return true;
    }
  }

  /** @param {unknown} reason */
  #disableStorefrontApi(reason) {
    try {
      window.sessionStorage.setItem(CM_SFAPI_SESSION_KEY, 'off');
    } catch {
      /* privater Modus o. ae. — dann eben pro Seitenaufruf */
    }
    console.info('[cart-items] Variantenwechsel laeuft in dieser Sitzung ueber die Ajax-API.', reason);
  }

  /**
   * Nur wenn alles eindeutig ist. Im Zweifel immer Ajax.
   * @param {any} cart
   * @param {any} line
   * @param {number} toVariantId
   */
  #canUseStorefrontApi(cart, line, toVariantId) {
    if (!this.#storefrontApiAllowed()) return false;
    if (typeof cart.token !== 'string' || !cart.token) return false;
    if (line.selling_plan_allocation) return false;
    if (cart.items.length > 100) return false;
    // Liegt die Zielvariante schon im Warenkorb, fuehrt add.js die Zeilen
    // eindeutig zusammen — das ueberlassen wir dem Ajax-Weg.
    if (cart.items.some((/** @type {any} */ item) => Number(item.variant_id) === toVariantId)) return false;
    return true;
  }

  /**
   * @param {string} query
   * @param {Record<string, any>} variables
   */
  async #storefrontRequest(query, variables) {
    const response = await cmFetch(`${window.location.origin}/api/${CM_SFAPI_VERSION}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) throw new Error(`Storefront API HTTP ${response.status}`);

    const json = await cmReadJson(response);
    if (!json) throw new Error('Storefront API: leere Antwort');
    if (Array.isArray(json.errors) && json.errors.length) {
      throw new Error(`Storefront API: ${json.errors[0]?.message ?? 'Fehler'}`);
    }

    return json.data ?? null;
  }

  /**
   * Tauscht die Variante innerhalb der bestehenden Zeile.
   * @param {any} cart
   * @param {any} line
   * @param {number} toVariantId
   * @returns {Promise<{ status: 'applied' | 'skipped' | 'ambiguous', notice?: string | null }>}
   */
  async #swapViaStorefrontApi(cart, line, toVariantId) {
    const cartId = `gid://shopify/Cart/${cart.token}`;
    const fromGid = cmVariantGid(line.variant_id);
    const toGid = cmVariantGid(toVariantId);

    /** @type {any[] | undefined} */
    let lines;
    try {
      const data = await this.#storefrontRequest(CM_SFAPI_LINES_QUERY, { id: cartId });
      lines = data?.cart?.lines?.nodes;
    } catch (error) {
      this.#disableStorefrontApi(error);
      return { status: 'skipped' };
    }

    if (!Array.isArray(lines)) {
      this.#disableStorefrontApi('Warenkorb ueber die Storefront API nicht lesbar');
      return { status: 'skipped' };
    }

    // Die Zeile muss eindeutig zuzuordnen sein: gleiche Variante, gleiche
    // Properties, gleiche Menge. Sonst lieber den sicheren Ajax-Weg.
    const candidates = lines.filter(
      (node) => node?.merchandise?.id === fromGid && cmAttributesMatch(node.attributes, line.properties)
    );
    if (candidates.length !== 1 || candidates[0].quantity !== line.quantity) return { status: 'skipped' };

    const target = candidates[0];
    const attributes = (Array.isArray(target.attributes) ? target.attributes : []).map(
      (/** @type {any} */ attribute) => ({
        key: String(attribute.key),
        value: attribute.value === null || attribute.value === undefined ? '' : String(attribute.value),
      })
    );

    let payload;
    try {
      const data = await this.#storefrontRequest(CM_SFAPI_LINE_VARIANT_MUTATION, {
        cartId,
        lines: [{ id: target.id, merchandiseId: toGid, quantity: line.quantity, attributes }],
      });
      payload = data?.cartLinesUpdate;
    } catch (error) {
      this.#disableStorefrontApi(error);
      // Kann trotzdem angekommen sein -> nachsehen statt raten.
      return this.#classifyUncertainSwap(line, toVariantId);
    }

    if (!payload) {
      this.#disableStorefrontApi('keine Antwort auf cartLinesUpdate');
      return this.#classifyUncertainSwap(line, toVariantId);
    }

    // Abgelehnt -> Warenkorb unveraendert. Der Ajax-Weg liefert dann die
    // passende, von Shopify formulierte Meldung (z. B. ausverkauft).
    if (Array.isArray(payload.userErrors) && payload.userErrors.length) return { status: 'skipped' };

    const after = payload.cart?.lines?.nodes;
    if (!Array.isArray(after)) return this.#classifyUncertainSwap(line, toVariantId);

    const hasTarget = after.some((node) => node?.merchandise?.id === toGid);
    const oldUnchanged = after.some((node) => node?.id === target.id && node?.merchandise?.id === fromGid);

    if (hasTarget && !oldUnchanged) {
      const warning = Array.isArray(payload.warnings)
        ? payload.warnings.find((/** @type {any} */ entry) => /MERCHANDISE|STOCK|QUANTITY/i.test(String(entry?.code ?? '')))
        : null;
      return { status: 'applied', notice: warning?.message ? String(warning.message) : null };
    }

    if (!hasTarget && oldUnchanged) {
      // Shop ignoriert die neue Variante -> Weg 1 dauerhaft aus.
      this.#disableStorefrontApi('merchandiseId wurde ignoriert');
      return { status: 'skipped' };
    }

    return { status: 'ambiguous' };
  }

  /**
   * Nach einer Antwort, die nicht eindeutig war: /cart.js entscheidet.
   * Die Zielvariante lag vorher nicht im Warenkorb (#canUseStorefrontApi),
   * ist sie jetzt da, kam der Wechsel also an.
   * @param {any} line
   * @param {number} toVariantId
   * @returns {Promise<{ status: 'applied' | 'skipped' | 'ambiguous', notice?: string | null }>}
   */
  async #classifyUncertainSwap(line, toVariantId) {
    try {
      const cart = await this.#getCartJson();
      const oldThere = cart.items.some((/** @type {any} */ item) => item.key === line.key);
      const targetThere = cart.items.some((/** @type {any} */ item) => Number(item.variant_id) === toVariantId);

      if (!oldThere && targetThere) return { status: 'applied', notice: null };
      if (oldThere && !targetThere) return { status: 'skipped' };
    } catch {
      /* faellt auf 'ambiguous' */
    }
    return { status: 'ambiguous' };
  }

  /* ------------------------------------------------- Requests & Zustand */

  /** @returns {Promise<any>} */
  async #getCartJson() {
    const response = await cmFetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);

    const cart = await cmReadJson(response);
    if (!cart || !Array.isArray(cart.items)) throw new Error('cart.js: unerwartete Antwort');
    return cart;
  }

  /**
   * POST an die Ajax-Cart-API. Wirft nur bei Netzfehler/Zeitlimit.
   * @param {string} url
   * @param {Record<string, any>} payload
   * @returns {Promise<{ ok: boolean, status: number, json: any }>}
   */
  async #postCart(url, payload) {
    const response = await cmFetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
    const json = await cmReadJson(response);
    const ok =
      response.ok &&
      json !== null &&
      !json.errors &&
      !(typeof json.status === 'number' && json.status >= 400);

    return { ok, status: response.status, json };
  }

  /**
   * Frischer Stand: /cart.js + alle Warenkorb-Sections, parallel.
   * @param {string[]} sectionIds
   */
  async #loadCartState(sectionIds) {
    const [cart, ...htmls] = await Promise.all([
      this.#getCartJson(),
      ...sectionIds.map((id) => sectionRenderer.getSectionHTML(id, false)),
    ]);

    /** @type {Record<string, string>} */
    const sections = {};
    sectionIds.forEach((id, index) => {
      if (htmls[index]) sections[id] = htmls[index];
    });

    return { cart, sections };
  }

  /**
   * @param {{ cart?: any, sections?: Record<string, string> | null }} state
   */
  async #applyCartState({ cart, sections }) {
    const html = sections?.[this.sectionId];

    if (html) {
      this.#lastAppliedHtml = html;
      await morphSection(this.sectionId, html, { mode: this.isDrawer ? 'hydration' : 'full' });
    }

    if (cart) this.#updateQuantitySelectors(cart);
    this.#updateCartQuantitySelectorButtonStates();
    this.#syncVariantSelects(true);
  }

  /**
   * Meldet den Wechsel an alle, die auf Warenkorb-Events hoeren (Cart-Icon,
   * Cart-Drawer, Upsell-Widget, Standard-Events). Die eigene Komponente
   * ignoriert ihr eigenes Event (siehe #handleCartUpdate).
   * @param {string} lineKey
   * @param {number} toVariantId
   * @param {number} quantity
   * @param {Promise<any>} promise
   */
  #dispatchVariantSwapEvent(lineKey, toVariantId, quantity, promise) {
    // Ohne Zuhoerer kein "Uncaught (in promise)" in der Konsole.
    promise.catch(() => {});

    const lineShapes = [
      [
        { id: lineKey, quantity: 0 },
        { merchandiseId: String(toVariantId), quantity },
      ],
      [{ id: lineKey, quantity }],
    ];

    for (const lines of lineShapes) {
      try {
        this.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines, promise }));
        return;
      } catch {
        /* naechste, einfachere Form versuchen */
      }
    }
  }

  /**
   * @param {{ resolve: Function, reject: Function }} deferred
   * @param {{ cart?: any, sections?: Record<string, string> | null }} state
   */
  #resolveSwapEvent(deferred, { cart, sections }) {
    if (!cart) {
      deferred.resolve({ detail: { sections: sections ?? {}, source: 'cart-items-component', didError: false } });
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: sections ?? {},
        items: cart.items,
        itemCount: Number(cart.item_count) || 0,
        source: 'cart-items-component',
        didError: false,
      },
    });
  }

  /**
   * Fokus zurueck (Tastatur), neue Zeile weich einblenden und sichtbar halten.
   * @param {number} toVariantId
   * @param {number} focusIndex
   * @param {boolean} restoreFocus
   * @param {string | null} notice
   */
  #afterVariantSwap(toVariantId, focusIndex, restoreFocus, notice) {
    const row = this.#findRowByVariant(toVariantId);
    if (!row) return;

    if (!prefersReducedMotion()) {
      row.classList.add('cart-items__table-row--variant-updated');
      onAnimationEnd(row, () => row.classList.remove('cart-items__table-row--variant-updated'));
    }

    if (restoreFocus) {
      const select = row.querySelector(`select[data-option-index="${focusIndex}"]`);
      if (select instanceof HTMLSelectElement) select.focus({ preventScroll: true });
    }

    this.#revealRow(row);

    if (notice) this.#showRowError(row, notice);
  }

  /**
   * Hat Shopify die Zeile nach oben sortiert (Ajax-Weg), wird sie in den
   * sichtbaren Bereich gescrollt, damit der Kunde sie nicht sucht.
   * @param {HTMLElement} row
   */
  #revealRow(row) {
    const scroller = row.closest('.cart-drawer__scroll');
    if (!(scroller instanceof HTMLElement)) return;

    const rowRect = row.getBoundingClientRect();
    const boxRect = scroller.getBoundingClientRect();
    if (rowRect.top >= boxRect.top && rowRect.bottom <= boxRect.bottom) return;

    scroller.scrollBy({
      top: rowRect.top - boxRect.top - 12,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /* ------------------------------------------------------- DOM-Helfer */

  #getSectionIdsToUpdate() {
    const sectionIds = new Set([this.sectionId]);
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.add(element.dataset.sectionId);
    }
    return Array.from(sectionIds);
  }

  async #waitForInflightRequests() {
    if (this.#inflightRequests.size === 0) return;
    await Promise.allSettled(Array.from(this.#inflightRequests));
  }

  /**
   * @param {HTMLElement} picker
   * @returns {HTMLSelectElement[]}
   */
  #getPickerSelects(picker) {
    return /** @type {HTMLSelectElement[]} */ (Array.from(picker.querySelectorAll('select[data-cm-variant-option]')));
  }

  /**
   * Setzt Dropdowns auf den gerenderten Server-Stand (data-cm-value).
   * @param {HTMLSelectElement[]} selects
   */
  #resetPickerSelects(selects) {
    for (const select of selects) {
      if (!select.isConnected) continue;
      const value = select.dataset.cmValue;
      if (value !== undefined && select.value !== value) select.value = value;
    }
  }

  /**
   * @param {boolean} [force] - auch waehrend eines laufenden Wechsels
   */
  #syncVariantSelects(force = false) {
    if (this.#variantSwapBusy && !force) return;
    this.#resetPickerSelects(
      /** @type {HTMLSelectElement[]} */ (Array.from(this.querySelectorAll('select[data-cm-variant-option]')))
    );
  }

  /** @returns {HTMLSelectElement[]} */
  #lockVariantSelects() {
    /** @type {HTMLSelectElement[]} */
    const locked = [];
    for (const select of this.querySelectorAll('select[data-cm-variant-option]')) {
      if (select instanceof HTMLSelectElement && !select.disabled) {
        select.disabled = true;
        locked.push(select);
      }
    }
    return locked;
  }

  /** @param {HTMLSelectElement[]} locked */
  #unlockVariantSelects(locked) {
    for (const select of locked) {
      if (select.isConnected) select.disabled = false;
    }
  }

  /**
   * @param {HTMLElement} row
   * @param {boolean} loading
   */
  #setRowLoading(row, loading) {
    if (!row.isConnected) return;
    row.classList.toggle('cart-items__table-row--variant-loading', loading);
    if (loading) {
      row.setAttribute('aria-busy', 'true');
    } else {
      row.removeAttribute('aria-busy');
    }
  }

  /** @param {number} variantId */
  #findRowByVariant(variantId) {
    const row = this.querySelector(`tr[data-key^="${Number(variantId)}:"]`);
    return row instanceof HTMLElement ? row : null;
  }

  /** @param {string} key */
  #findRowByKey(key) {
    for (const row of this.querySelectorAll('tr[data-key]')) {
      if (row instanceof HTMLElement && row.dataset.key === key) return row;
    }
    return null;
  }

  /**
   * Meldung in der Fehlerzeile der Produktzeile (dieselbe, die das Theme fuer
   * Mengenfehler nutzt). Der naechste Morph blendet sie wieder aus.
   * @param {HTMLElement | null} row
   * @param {string | null | undefined} message
   */
  #showRowError(row, message) {
    if (!(row instanceof HTMLElement) || !row.isConnected || !message) return;
    const container = row.querySelector('.cart-items__error');
    const text = row.querySelector('.cart-item__error-text');
    if (!container || !text) return;

    text.textContent = message;
    container.classList.remove('hidden');
  }

  /** @param {HTMLElement} row */
  #hideRowError(row) {
    row.querySelector('.cart-items__error')?.classList.add('hidden');
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    // CM: Ein Mengen-Request, der waehrend eines Variantenwechsels endet,
    // darf die Sperre nicht vorzeitig aufheben.
    if (this.#variantSwapBusy) return;
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
